/** Routes and the permission gate in front of each screen. */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppShell } from './AppShell';
import { LoginPage } from './LoginPage';
import { ChangePasswordPage } from './ChangePasswordPage';
import { DashboardPage } from './DashboardPage';
import { CustomersPage } from './customers/CustomersPage';
import { CustomerFormPage } from './customers/CustomerFormPage';
import { CustomerDetailPage } from './customers/CustomerDetailPage';
import { LeadsPage } from './leads/LeadsPage';
import { LeadFormPage } from './leads/LeadFormPage';
import { LeadDetailPage } from './leads/LeadDetailPage';
import { SalesOrdersPage } from './sales-orders/SalesOrdersPage';
import { SalesOrderDetailPage } from './sales-orders/SalesOrderDetailPage';
import { BillingPage } from './billing/BillingPage';
import { BillingDetailPage } from './billing/BillingDetailPage';
import { ManualBillingPage } from './billing/ManualBillingPage';
import { CustomerStatementPage } from './billing/CustomerStatementPage';
import { ProjectsPage } from './projects/ProjectsPage';
import { ProjectDetailPage } from './projects/ProjectDetailPage';
import { PayablesPage } from './payables/PayablesPage';
import { ArAgingPage } from './reports/ArAgingPage';
import { ApReportPage } from './reports/ApReportPage';
import { ProfitabilityPage } from './reports/ProfitabilityPage';
import { SuppliersPage } from './suppliers/SuppliersPage';
import { PriceBookPage } from './suppliers/PriceBookPage';
import { PriceBookItemPage } from './suppliers/PriceBookItemPage';
import { QuotationsPage } from './purchasing/QuotationsPage';
import { PurchaseOrdersPage } from './purchasing/PurchaseOrdersPage';
import { ApAccountsPage } from './management/ApAccountsPage';
import { UsersPage } from './management/UsersPage';
import { ApiClientsPage } from './management/ApiClientsPage';
import { RolesPage } from './management/RolesPage';
import { ParametersPage } from './management/ParametersPage';
import { CustomFieldsPage } from './management/CustomFieldsPage';
import { TaxSettingsPage } from './management/TaxSettingsPage';
import { CompanyProfilePage } from './management/CompanyProfilePage';
import { AuditLogPage } from './management/AuditLogPage';
import { Alert, Skeleton } from '../components/ui';

function Protected({
  children,
  permissions,
}: {
  children: ReactNode;
  permissions?: string[];
}): JSX.Element {
  const { user, loading, can } = useAuth();

  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustResetPassword) return <Navigate to="/change-password" replace />;
  if (permissions && !can(...permissions)) {
    return (
      <AppShell>
        <Alert tone="critical">
          Your role does not give you access to this page. Ask an administrator if you need it.
        </Alert>
      </AppShell>
    );
  }
  return <AppShell>{children}</AppShell>;
}

export function App(): JSX.Element {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={loading ? <div /> : user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/change-password"
        element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/"
        element={
          <Protected permissions={['dashboard.view']}>
            <DashboardPage />
          </Protected>
        }
      />

      <Route
        path="/customers"
        element={
          <Protected permissions={['customers.view']}>
            <CustomersPage />
          </Protected>
        }
      />
      <Route
        path="/customers/new"
        element={
          <Protected permissions={['customers.create']}>
            <CustomerFormPage />
          </Protected>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <Protected permissions={['customers.view']}>
            <CustomerDetailPage />
          </Protected>
        }
      />
      <Route
        path="/customers/:id/edit"
        element={
          <Protected permissions={['customers.update']}>
            <CustomerFormPage />
          </Protected>
        }
      />

      <Route
        path="/leads"
        element={
          <Protected permissions={['leads.view_own', 'leads.view_all']}>
            <LeadsPage />
          </Protected>
        }
      />
      <Route
        path="/leads/new"
        element={
          <Protected permissions={['leads.create']}>
            <LeadFormPage />
          </Protected>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <Protected permissions={['leads.view_own', 'leads.view_all']}>
            <LeadDetailPage />
          </Protected>
        }
      />
      <Route
        path="/leads/:id/edit"
        element={
          <Protected permissions={['leads.update']}>
            <LeadFormPage />
          </Protected>
        }
      />

      <Route
        path="/sales-orders"
        element={
          <Protected permissions={['salesorders.view']}>
            <SalesOrdersPage />
          </Protected>
        }
      />
      <Route
        path="/sales-orders/:id"
        element={
          <Protected permissions={['salesorders.view']}>
            <SalesOrderDetailPage />
          </Protected>
        }
      />

      <Route
        path="/billing"
        element={
          <Protected permissions={['billing.view']}>
            <BillingPage />
          </Protected>
        }
      />
      <Route
        path="/billing/manual"
        element={
          <Protected permissions={['billing.create']}>
            <ManualBillingPage />
          </Protected>
        }
      />
      <Route
        path="/billing/customers/:id/statement"
        element={
          <Protected permissions={['billing.view']}>
            <CustomerStatementPage />
          </Protected>
        }
      />
      <Route
        path="/billing/:id"
        element={
          <Protected permissions={['billing.view']}>
            <BillingDetailPage />
          </Protected>
        }
      />

      <Route
        path="/projects"
        element={
          <Protected permissions={['projects.view_all', 'projects.view_assigned']}>
            <ProjectsPage />
          </Protected>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <Protected permissions={['projects.view_all', 'projects.view_assigned']}>
            <ProjectDetailPage />
          </Protected>
        }
      />

      <Route
        path="/payables"
        element={
          <Protected permissions={['ap.view_all', 'ap.view_own', 'ap.create']}>
            <PayablesPage />
          </Protected>
        }
      />

      <Route
        path="/suppliers"
        element={
          <Protected permissions={['suppliers.view']}>
            <SuppliersPage />
          </Protected>
        }
      />
      <Route
        path="/suppliers/price-book"
        element={
          <Protected permissions={['pricebook.view']}>
            <PriceBookPage />
          </Protected>
        }
      />
      <Route
        path="/suppliers/price-book/:id"
        element={
          <Protected permissions={['pricebook.view']}>
            <PriceBookItemPage />
          </Protected>
        }
      />
      <Route
        path="/sales-orders/quotations"
        element={
          <Protected permissions={['suppliers.view']}>
            <QuotationsPage />
          </Protected>
        }
      />
      <Route
        path="/sales-orders/purchase-orders"
        element={
          <Protected permissions={['po.view']}>
            <PurchaseOrdersPage />
          </Protected>
        }
      />
      <Route
        path="/management/ap-accounts"
        element={
          <Protected permissions={['parameters.manage']}>
            <ApAccountsPage />
          </Protected>
        }
      />

      <Route
        path="/reports/ar-aging"
        element={
          <Protected permissions={['reports.ar']}>
            <ArAgingPage />
          </Protected>
        }
      />
      <Route
        path="/reports/ap"
        element={
          <Protected permissions={['reports.ap']}>
            <ApReportPage />
          </Protected>
        }
      />
      <Route
        path="/reports/profitability"
        element={
          <Protected permissions={['reports.profitability']}>
            <ProfitabilityPage />
          </Protected>
        }
      />

      <Route
        path="/management/api-keys"
        element={
          <Protected permissions={['users.manage']}>
            <ApiClientsPage />
          </Protected>
        }
      />
      <Route
        path="/management/users"
        element={
          <Protected permissions={['users.manage']}>
            <UsersPage />
          </Protected>
        }
      />
      <Route
        path="/management/roles"
        element={
          <Protected permissions={['roles.manage']}>
            <RolesPage />
          </Protected>
        }
      />
      <Route
        path="/management/parameters"
        element={
          <Protected permissions={['parameters.manage']}>
            <ParametersPage />
          </Protected>
        }
      />
      <Route
        path="/management/custom-fields"
        element={
          <Protected permissions={['parameters.manage']}>
            <CustomFieldsPage />
          </Protected>
        }
      />
      <Route
        path="/management/tax"
        element={
          <Protected permissions={['settings.manage']}>
            <TaxSettingsPage />
          </Protected>
        }
      />
      <Route
        path="/management/company"
        element={
          <Protected permissions={['settings.manage']}>
            <CompanyProfilePage />
          </Protected>
        }
      />
      <Route
        path="/management/audit"
        element={
          <Protected permissions={['audit.view']}>
            <AuditLogPage />
          </Protected>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
